﻿import {StringNamedValue} from "@2d-front-monorepo/core";
import React, {JSX, useMemo, useRef} from "react";
import {useArrayOnChange} from "../helpers/useArrayOnChange";
import {useSelected} from "../helpers/useSelected";
import Checkbox from "./Checkbox";
import Radio from "./Radio";

export interface OptionListEntry extends StringNamedValue {
	hint?: string;
	description?: string;
	descriptionOpen?: boolean;
	disabled?: boolean;
}

export interface OptionListProps {
	name: string,
	values: string[],
	items: OptionListEntry[];
	column?: string,
	disabled?: boolean;
	hidden?: boolean;
	onChange?: (values: number[]) => void;
	onBlur?: () => void;
	ref?: React.RefObject<HTMLDivElement | null>;
	multiple?: boolean;
}

export default function OptionList(props: OptionListProps): JSX.Element {
	const {
		name,
		values,
		items,
		column,
		hidden,
		disabled,
		onChange,
		onBlur,
		ref,
		multiple
	} = props;

	const klass = useMemo(() => "option-list" + (column ? ` ${column}` : ""), [column]);
	const fallbackRef = useRef<HTMLDivElement>(null);
	const innerRef = ref ?? fallbackRef;
	const isDisabled = disabled ?? false;

	const selected = useSelected(values, multiple);
	const internalOnChange = useArrayOnChange(innerRef, onChange);

	if (hidden) {
		return <></>;
	}

	if (multiple) {
		return <div className={klass} ref={innerRef}>
			{items.map((item, index) => <Checkbox
				label={item.label}
				value={item.value}
				key={index}
				arrayKey={index.toString()}
				name={name}
				hint={item.hint}
				description={item.description}
				checked={selected(item.value)}
				disabled={isDisabled || item?.disabled}
				onChange={internalOnChange}
				onBlur={onBlur}/>)}
		</div>;
	}

	return <div className={klass} ref={innerRef}>
		{items.map((item, index) => <Radio
			label={item.label}
			value={item.value}
			key={index}
			arrayKey={index.toString()}
			name={name}
			hint={item.hint}
			description={item.description}
			descriptionOpen={item.descriptionOpen}
			checked={selected(item.value)}
			disabled={isDisabled || item?.disabled}
			onChange={internalOnChange}
			onBlur={onBlur}/>)}
	</div>;
};
